import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AppointmentDto,
  AppointmentStatus,
  PropertyApplicationDto,
  PropertyApplicationPendingActionType,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import { LuxonDatePipe } from '@workspace/ng-aura';
import { filter, tap } from 'rxjs';
import { PropertyApplicationTimelineComponent } from '../timeline/property-application-timeline.component';
import {
  PropertyApplicationWithdrawDialogComponent,
  PropertyApplicationWithdrawDialogData,
  PropertyApplicationWithdrawDialogResult,
} from '../withdraw-dialog/property-application-withdraw-dialog.component';

@Component({
  selector: 'etxe-property-application-action-section',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    LuxonDatePipe,
    PropertyApplicationTimelineComponent,
  ],
  templateUrl: './property-application-action-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyApplicationActionSectionComponent {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly status = input.required<PropertyApplicationStatus>();
  readonly application = input<PropertyApplicationDto | null>(null);
  readonly appointment = input<AppointmentDto | null>(null);
  readonly property = input<PropertyDto | null>(null);

  readonly openDiscussion = output<void>();
  readonly viewProperty = output<void>();
  readonly openAgenda = output<void>();
  readonly completeVisit = output<void>();
  readonly adjustFinalOffer = output<void>();
  readonly withdraw = output<string | undefined>();

  readonly canWithdraw = computed(() => {
    const s = this.status();
    return (
      s === PropertyApplicationStatus.PENDING ||
      s === PropertyApplicationStatus.VISIT_INVITED ||
      s === PropertyApplicationStatus.VISIT_CONFIRMED ||
      s === PropertyApplicationStatus.VISITED ||
      s === PropertyApplicationStatus.OFFER_SUBMITTED
    );
  });

  readonly createdAt = computed(() => this.application()?.createdAt ?? null);

  readonly finalOfferSubmittedAt = computed(() => {
    return this.application()?.engagement?.finalOfferSubmittedAt ?? null;
  });

  readonly statusConfig = computed(() => {
    const s = this.status();
    switch (s) {
      case PropertyApplicationStatus.PENDING:
        return {
          title: 'Candidature en attente d’examen',
          description:
            'Votre proposition a bien été transmise au propriétaire. Il examine actuellement les dossiers des agents avant de proposer des créneaux de visite.',
          badgeBg: 'bg-amber-3 text-amber-11 border-amber-6',
          badgeText: 'En cours d’examen',
          icon: 'schedule',
        };
      case PropertyApplicationStatus.VISIT_INVITED:
        return {
          title: 'Invitation à visiter le bien reçue',
          description:
            'Le propriétaire souhaite vous rencontrer ! Il vous a proposé des créneaux de visite. Rendez-vous dans la messagerie pour convenir de la date.',
          badgeBg: 'bg-orange-3 text-orange-11 border-orange-6',
          badgeText: 'Créneaux proposés',
          icon: 'event_available',
        };
      case PropertyApplicationStatus.VISIT_CONFIRMED:
        return {
          title: 'Visite sur place confirmée',
          description:
            'Votre rendez-vous sur place est planifié. Préparez votre estimation détaillée et votre argumentaire commercial pour convaincre le vendeur.',
          badgeBg: 'bg-indigo-3 text-indigo-11 border-indigo-6',
          badgeText: 'Rendez-vous planifié',
          icon: 'event_note',
        };
      case PropertyApplicationStatus.VISITED:
        return {
          title: 'Avis de valeur et offre finale',
          description:
            'La visite du bien a été réalisée. Vous disposez de 72h pour formaliser votre avis de valeur et transmettre votre offre d’honoraires définitive au propriétaire.',
          badgeBg: 'bg-indigo-3 text-indigo-11 border-indigo-6',
          badgeText: 'Avis de valeur & offre finale',
          icon: 'description',
        };
      case PropertyApplicationStatus.OFFER_SUBMITTED:
        return {
          title: 'Avis de valeur et offre finale transmis',
          description:
            'Votre avis de valeur et votre proposition d’accompagnement définitive sont transmis au propriétaire. Il effectue son choix final pour l’attribution du mandat.',
          badgeBg: 'bg-indigo-3 text-indigo-11 border-indigo-6',
          badgeText: 'Avis et offre finale déposés',
          icon: 'task_alt',
        };
      case PropertyApplicationStatus.ACCEPTED:
        return {
          title: 'Mandat attribué avec succès !',
          description:
            'Félicitations ! Le propriétaire a retenu votre candidature pour commercialiser son bien immobilier.',
          badgeBg: 'bg-jade-3 text-jade-11 border-jade-6',
          badgeText: 'Mandat remporté',
          icon: 'verified',
        };
      case PropertyApplicationStatus.REJECTED:
        return {
          title: 'Candidature non retenue',
          description:
            'Le propriétaire a choisi de confier son mandat à un confrère pour ce projet immobilier.',
          badgeBg: 'bg-red-3 text-red-11 border-red-6',
          badgeText: 'Non retenue',
          icon: 'cancel',
        };
      case PropertyApplicationStatus.EXPIRED:
        return {
          title: 'Démarche expirée',
          description:
            'Le délai accordé au vendeur pour faire son choix est arrivé à échéance sans sélection.',
          badgeBg: 'bg-neutral-3 text-neutral-11 border-neutral-6',
          badgeText: 'Expirée',
          icon: 'timer_off',
        };
      case PropertyApplicationStatus.CANCELLED:
        return {
          title: 'Candidature retirée ou annulée',
          description: 'Cette démarche a été interrompue. Votre place a été libérée pour les autres agents.',
          badgeBg: 'bg-neutral-3 text-neutral-11 border-neutral-6',
          badgeText: 'Retirée',
          icon: 'block',
        };

      default:
        return {
          title: 'Suivi du projet',
          description: 'Suivez l’avancement de votre candidature.',
          badgeBg: 'bg-neutral-3 text-neutral-11 border-neutral-6',
          badgeText: 'En cours',
          icon: 'info',
        };
    }
  });

  readonly pendingAction = computed(() => {
    return this.application()?.pendingAction || null;
  });

  readonly pendingActionLabel = computed(() => {
    const action = this.pendingAction();
    if (!action) return null;
    switch (action.actionType) {
      case PropertyApplicationPendingActionType.VISIT_PROPOSAL:
        return 'Proposition de créneaux de visite';
      case PropertyApplicationPendingActionType.VISIT_RESCHEDULE:
        return 'Demande de reprogrammation de visite';
      case PropertyApplicationPendingActionType.FINAL_OFFER:
        return 'Avis de valeur et offre finale';
      case PropertyApplicationPendingActionType.MANDATE_SELECTION:
        return 'Sélection finale du mandat';
      default:
        return 'Action en attente';
    }
  });

  // Check if appointment is upcoming and NOT already in the past
  readonly isAppointmentUpcoming = computed<boolean>(() => {
    const appt = this.appointment();
    if (!appt) return false;

    // If marked completed or cancelled, do not show in upcoming agenda
    if (
      appt.status === AppointmentStatus.COMPLETED ||
      appt.status === AppointmentStatus.CANCELLED ||
      appt.status === AppointmentStatus.NO_SHOW
    ) {
      return false;
    }

    const now = Date.now();
    const confirmedSlot = appt.confirmedSlot;
    if (confirmedSlot) {
      const endOrStart = confirmedSlot.endAt || confirmedSlot.startAt;
      return new Date(endOrStart).getTime() > now;
    }

    // If slots are proposed but not confirmed, check if at least one is in the future
    if (appt.proposedSlots && appt.proposedSlots.length > 0) {
      return appt.proposedSlots.some((slot) => {
        const time = new Date(slot.endAt || slot.startAt).getTime();
        return time > now;
      });
    }

    return false;
  });

  readonly confirmedSlot = computed(() => {
    return this.appointment()?.confirmedSlot || null;
  });

  readonly isVisitPassed = computed<boolean>(() => {
    const appt = this.appointment();
    if (!appt || appt.status !== AppointmentStatus.CONFIRMED) return false;
    const confirmedSlot = appt.confirmedSlot;
    if (confirmedSlot) {
      return new Date(confirmedSlot.startAt).getTime() <= Date.now();
    }
    return false;
  });

  onWithdrawClick(): void {
    const dialogRef = this.dialog.open<
      PropertyApplicationWithdrawDialogComponent,
      PropertyApplicationWithdrawDialogData,
      PropertyApplicationWithdrawDialogResult
    >(PropertyApplicationWithdrawDialogComponent, {
      data: {
        hasAppointment: Boolean(this.appointment()),
      },
      width: '560px',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result): result is PropertyApplicationWithdrawDialogResult => Boolean(result?.confirmed)),
        tap((result) => {
          this.withdraw.emit(result.reason);
        }),
      )
      .subscribe();
  }
}
